import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';

import {TerraUtils} from '../../TerraUtils';
import {LocationDetailsResponse} from 'src/app/shared/models/LocationDetailsResponse.model';
import {CountryModel} from '../../models/CountryModel';
import {ResourceService} from 'src/app/services/resource.service';
import {AddressFields, AddressFormSettings} from './address-form.settings';
import {CustomValidators} from '../../validators/custom.validators';

@Component({
  selector: 'terra-address-form',
  templateUrl: './address-form.component.html',
  styleUrls: ['./address-form.component.scss']
})
export class AddressFormComponent extends OnDestroyMixin implements OnInit, OnChanges {
  private static nextId = 0;

  get instanceId() {
    return AddressFormComponent.nextId;
  }

  @Input() externalForm: UntypedFormGroup;

  @Input() partialFormName: string;

  @Input() locationDetailsResponse: LocationDetailsResponse;

  @Input() formSettings: AddressFormSettings;

  @Input() streetLabel: string = null;
  @Input() street2Label: string = null;
  @Input() lockUsaCountry = false;

  @Input() requireMark: string = null;
  @Input() optionalMark = '(optional)';


  @Output() formReady = new EventEmitter<UntypedFormGroup>();

  allCountries = this.resourceService.allCountries;
  allStates = this.resourceService.allStates;
  countryCodes = TerraUtils.consts.countryCode;
  AddressFields = AddressFields;
  USCountryId = this.resourceService.getCountryByCode(this.countryCodes.US).id;

  get addressForm() {
    return this.externalForm.get(this.partialFormName) as UntypedFormGroup;
  }

  constructor(private fb: UntypedFormBuilder, private resourceService: ResourceService) {
    super();
    AddressFormComponent.nextId++;
  }

  ngOnInit() {
    this.generateForm();
  }

  textForFiled(fieldName: string, isRequired: boolean) {
    // !formSettings.toRequireField(AddressFields.Street) ? '(optional)' : ''
    if (!isRequired && !!this.optionalMark) {
      return `${fieldName} ${this.optionalMark}`;
    }

    if (isRequired && !!this.requireMark) {
      return `${this.requireMark} ${fieldName}`;
    }

    return fieldName;
  }

  getValidators(maxLength: number, isRequired: boolean) {
    const validatorArray = [];

    if (maxLength > 0) {
      validatorArray.push(Validators.maxLength(maxLength));
    }

    if (isRequired) {
      validatorArray.push(Validators.required);
    }

    return validatorArray;
  }

  generateForm() {
    // Sometimes the initial state should be disabled
    let isDisabled = false;
    if (this.externalForm.get(this.partialFormName)) {
      isDisabled = this.externalForm.get(this.partialFormName).disabled;
    }

    this.externalForm.setControl(
      this.partialFormName,
      this.fb.group({
        id: null,
        streetName: [null, this.getValidators(TerraUtils.consts.validators.STREET_LENGTH, this.isFieldRequired(AddressFields.Street))],
        street2Name: [null, this.getValidators(TerraUtils.consts.validators.STREET_LENGTH, this.isFieldRequired(AddressFields.Street2))],
        houseNumber: [null, this.getValidators(TerraUtils.consts.validators.HOUSE_NUMBER_LENGTH, this.isFieldRequired(AddressFields.HouseNumber))],
        cityName: [null, this.getValidators(TerraUtils.consts.validators.CITY_LENGTH, this.isFieldRequired(AddressFields.City))],
        postalCode: [null, this.getPostalCodeValidators(this.formSettings.toValidatePostalCodePattern, this.isFieldRequired(AddressFields.PostalCode))],
        stateName: [null, CustomValidators.requiredIf(() => this.isCountryUnitedStates() && this.formSettings.stateRequiredWhileUs)],
        countryId: [this.lockUsaCountry ? this.USCountryId : null, CustomValidators.requiredIf(() => this.isCountryRequired())]
      })
    );

    if (this.formSettings.toValidatePostalCodePattern) {
      this.addressForm.get('countryId').valueChanges.pipe(
        untilComponentDestroyed(this),
      ).subscribe((countryId) => {
        if (countryId === this.USCountryId) {
          this.addressForm.get('postalCode').setValidators(this.getPostalCodeValidators(true, this.isFieldRequired(AddressFields.PostalCode)));
        } else {
          this.addressForm.get('postalCode').setValidators(this.getPostalCodeValidators(false, this.isFieldRequired(AddressFields.PostalCode)));
        }

        this.addressForm.get('postalCode').updateValueAndValidity();
      });
    }

    if (this.locationDetailsResponse) {
      this.addressForm.patchValue(this.locationDetailsResponse);
      this.addressForm.updateValueAndValidity();
    }

    if (isDisabled) {
      this.addressForm.disable();
    }

    this.subscribeToAddressChanges();
    this.subscribeToCountryChanges();
    this.formReady.next(this.addressForm);
  }

  isCountryRequired(): boolean {
    const address = this.addressForm.value;
    return this.formSettings.toRequireField(AddressFields.Country) || address.streetName ||
      address.street2Name || address.houseNumber || address.cityName || address.postalCode;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.locationDetailsResponse && !changes.locationDetailsResponse.firstChange && changes.locationDetailsResponse.currentValue) {
      this.addressForm.patchValue(changes.locationDetailsResponse.currentValue);
    }
  }

  conutryTrackByFn(index, item: CountryModel) {
    return index; // or item.id
  }

  /** get a list of all postal code validators.
   * @param validatePattern - In some use cases we also need to validate the postal code pattern.
   * @param isRequired To require postal code
   */
  private getPostalCodeValidators(validatePattern: boolean = false, isRequired: boolean = false) {
    return validatePattern ?
      [...this.getValidators(TerraUtils.consts.validators.ZIP_LENGTH, isRequired),
        Validators.pattern(TerraUtils.consts.validators.POSTAL_CODE_PATTERN)] :
      this.getValidators(TerraUtils.consts.validators.ZIP_LENGTH, isRequired);
  }

  private isCountryUnitedStates() {
    return this.addressForm.get('countryId').value === this.resourceService.getCountryByCode(TerraUtils.consts.countryCode.US).id;
  }

  /** Because the country has a requiredIf validator,
   * and it depends on the values of other fields, when each of the other fields change, we should update the validity of the country.
   */
  subscribeToAddressChanges() {
    this.addressForm.valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe(address => {
        this.addressForm.get('countryId').updateValueAndValidity(
          {emitEvent: false /** This is crucial because othrwise it will cause an infinite loop */});
      });
  }

  subscribeToCountryChanges() {
    this.addressForm.get('countryId').valueChanges.pipe(untilComponentDestroyed(this))
      .subscribe(countryId => {
        if (countryId !== this.USCountryId) {
          (this.addressForm.get('stateName') as UntypedFormControl).reset(null, {emitEvent: false});
        }
      });
  }

  isFieldRequired(addressField: AddressFields): boolean {
    return this.formSettings.toShowField(addressField) && this.formSettings.toRequireField(addressField);
  }
}
